/**
 * Caption / WebVTT utilities — pure browser-side helpers used by the
 * transcript drawer and inline transcript display.
 */

/**
 * True if the given string looks like a WebVTT document.
 * Cheap heuristic: starts with "WEBVTT" header (per spec).
 */
export function looksLikeVtt(text) {
  if (!text || typeof text !== 'string') return false;
  return /^WEBVTT/m.test(text.trimStart().slice(0, 16));
}

/**
 * Convert a WebVTT document to plain text. Preserves cue boundaries as
 * newlines so consumers that split on \n get one paragraph per spoken cue.
 * Multi-line text within a cue is collapsed to a space.
 *
 * Returns the input unchanged if it doesn't look like VTT — useful for
 * legacy `asset.transcript` fields that were saved as plain text.
 */
export function vttToPlainText(vtt) {
  if (!vtt) return '';
  if (!looksLikeVtt(vtt)) return vtt;

  const lines = vtt.split(/\r?\n/).map((l) => l.trim());
  const cues = [];
  let currentCue = [];
  let inCueText = false;

  const flush = () => {
    if (currentCue.length) {
      cues.push(currentCue.join(' ').replace(/\s+/g, ' ').trim());
      currentCue = [];
    }
    inCueText = false;
  };

  for (const line of lines) {
    if (!line) { flush(); continue; }
    if (/^WEBVTT/.test(line)) continue;
    if (/^NOTE\b/.test(line)) continue;
    if (line.includes('-->')) { inCueText = true; continue; }
    if (!inCueText) continue; // skip cue identifiers
    currentCue.push(line);
  }
  flush();
  return cues.filter(Boolean).join('\n').trim();
}

// VTT timestamp ("HH:MM:SS.mmm" or "MM:SS.mmm") → seconds.
function vttTimeToSeconds(ts) {
  const parts = ts.split(':').map(Number);
  if (parts.some((n) => Number.isNaN(n))) return 0;
  return parts.reduce((acc, p) => acc * 60 + p, 0);
}

/**
 * Parse a WebVTT document into [{start, text}] — `start` in seconds, `text`
 * the plain cue text (multi-line collapsed, inline word-timing tags stripped).
 * Non-VTT input yields []. Used to locate the spoken moment behind a
 * transcript search hit so the player can seek to it.
 */
export function parseVttCues(vtt) {
  if (!looksLikeVtt(vtt)) return [];
  const lines = vtt.split(/\r?\n/).map((l) => l.trim());
  const cues = [];
  let current = null;
  const flush = () => {
    if (current && current.text.length) {
      cues.push({
        start: current.start,
        text: current.text.join(' ').replace(/\s+/g, ' ').trim(),
      });
    }
    current = null;
  };
  for (const line of lines) {
    if (!line) { flush(); continue; }
    if (/^WEBVTT/.test(line)) continue;
    if (/^NOTE\b/.test(line)) continue;
    if (line.includes('-->')) {
      const start = line.split('-->')[0].trim().split(/\s+/)[0];
      current = { start: vttTimeToSeconds(start), text: [] };
      continue;
    }
    if (!current) continue; // cue identifier line, before the timing line
    current.text.push(line.replace(/<\d{2}:\d{2}:\d{2}\.\d{3}>/g, ''));
  }
  flush();
  return cues;
}

/**
 * Convert a WebVTT document to SubRip (SRT) format.
 *
 * Differences SRT vs VTT this function handles:
 *   - WEBVTT header / NOTE blocks → stripped
 *   - Timestamp millisecond separator: "." → ","
 *   - Sequential numeric cue identifiers (regenerated from scratch)
 *   - Cues separated by blank line
 *
 * Assumes Bunny's auto-captions are well-formed VTT with HH:MM:SS.mmm
 * timestamps. If a cue uses MM:SS.mmm (no hour), prefixes "00:".
 */
export function vttToSrt(vtt) {
  if (!vtt) return '';

  const lines = vtt.split(/\r?\n/).map((l) => l.trim());
  const cues = [];
  let current = null;

  const normalizeTimestamp = (ts) => {
    // Add hours segment if missing
    const padded = /^\d{2}:\d{2}\.\d{3}$/.test(ts) ? `00:${ts}` : ts;
    // Replace ms decimal separator
    return padded.replace(/(\d{2}:\d{2}:\d{2})\.(\d{3})/, '$1,$2');
  };

  for (const line of lines) {
    if (!line) {
      if (current && current.text.length) cues.push(current);
      current = null;
      continue;
    }
    if (/^WEBVTT/.test(line)) continue;
    if (/^NOTE\b/.test(line)) continue;

    if (line.includes('-->')) {
      const [start, end] = line.split('-->').map((p) => p.trim().split(' ')[0]);
      current = {
        timestamp: `${normalizeTimestamp(start)} --> ${normalizeTimestamp(end)}`,
        text: [],
      };
      continue;
    }

    if (current) {
      current.text.push(line);
    }
    // Lines before the timestamp are cue identifiers — skip; SRT generates fresh ones.
  }
  if (current && current.text.length) cues.push(current);

  return cues
    .map((c, i) => `${i + 1}\n${c.timestamp}\n${c.text.join('\n')}`)
    .join('\n\n');
}

/**
 * True if the given string looks like a SubRip (SRT) document.
 * SRT timestamps use a comma millisecond separator (00:00:01,000) — the one
 * reliable feature that distinguishes it from WebVTT (which uses a dot).
 */
export function looksLikeSrt(text) {
  if (!text || typeof text !== 'string') return false;
  if (looksLikeVtt(text)) return false;
  return /\d{2}:\d{2}:\d{2},\d{3}\s*-->/.test(text);
}

/**
 * Convert a SubRip (SRT) document to WebVTT. Just prepends the WEBVTT header
 * and swaps the comma millisecond separator for a dot — VTT tolerates SRT's
 * numeric cue identifiers as-is, so nothing else needs stripping.
 */
export function srtToVtt(srt) {
  if (!srt) return '';
  const body = srt
    .replace(/\r+/g, '')
    .replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2')
    .trim();
  return `WEBVTT\n\n${body}\n`;
}

/**
 * Estimate the number of cues in a VTT document. Cheap, used for the
 * drawer subtitle ("12 cues").
 */
export function countVttCues(vtt) {
  if (!vtt) return 0;
  const matches = vtt.match(/-->/g);
  return matches ? matches.length : 0;
}

// seconds → "HH:MM:SS.mmm" (WebVTT timestamp). Mirrors the server-side
// formatter in functions/transcription/wordsToVtt.js.
function formatVttTime(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  const ms = Math.round((s - Math.floor(s)) * 1000);
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}.${pad(ms, 3)}`;
}

/**
 * Render a flat word-level array into WORD-LEVEL WebVTT: the same readable cue
 * grouping as the server (≤~5 s / ≤12 words / sentence breaks), but with an
 * inline WebVTT cue timestamp (`<00:00:15.700>`) before every word after the
 * first — the YouTube/karaoke format. Players show normal captions; the tags
 * carry per-word timing.
 *
 * @param {Array<{word:string,start:number,end:number}>} words
 * @returns {string} WebVTT document with inline word timestamps
 */
export function wordsToInlineVtt(words, opts = {}) {
  const maxSeconds = opts.maxSeconds || 5;
  const maxWords = opts.maxWords || 12;
  const list = Array.isArray(words) ? words.filter((w) => w && typeof w.word === 'string') : [];
  if (!list.length) return 'WEBVTT\n';

  const endsSentence = (w) => /[.!?]["')\]]?$/.test(w);
  const cues = [];
  let cue = null;
  for (const w of list) {
    if (!cue) {
      cue = { start: w.start, end: w.end, words: [w] };
      continue;
    }
    cue.words.push(w);
    cue.end = w.end;
    const tooLong = (cue.end - cue.start) >= maxSeconds || cue.words.length >= maxWords;
    if (endsSentence(w.word) || tooLong) {
      cues.push(cue);
      cue = null;
    }
  }
  if (cue) cues.push(cue);

  const renderCue = (c) => {
    // First word inherits the cue start; each later word gets an inline tag.
    const text = c.words
      .map((w, i) => (i === 0 ? w.word : `<${formatVttTime(w.start)}>${w.word}`))
      .join(' ');
    return `${formatVttTime(c.start)} --> ${formatVttTime(c.end)}\n${text}`;
  };
  return `WEBVTT\n\n${cues.map(renderCue).join('\n\n')}\n`;
}
